import { useParams, useNavigate } from "react-router-dom";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../db";
import Sidebar from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { ArrowLeft, User, Calendar, DollarSign, UserPlus, Trash2, CheckCircle } from "lucide-react";
import SyncIndicator from "@/components/SyncIndicator";
import { syncService } from "../services/SyncService";
import { toast } from "sonner";
import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatRupees } from "../utils/currency";
import PropTypes from 'prop-types';
import { getDisplayAge } from "../utils/patient";

export default function FacilityChargeDetail({ onLogout }) {
    const { chargeId } = useParams();
    const navigate = useNavigate();
    const [showPayDialog, setShowPayDialog] = useState(false);
    const [paymentDate, setPaymentDate] = useState("");

    // Get current doctor ID from localStorage
    const doctorId = localStorage.getItem('user_id');

    const data = useLiveQuery(async () => {
        if (!chargeId) return null;
        const facility_charge = await db.facility_charges.get(chargeId);
        if (!facility_charge) return null;

        // Get appointment
        const appointment = facility_charge.appointment_id
            ? await db.appointments.get(facility_charge.appointment_id)
            : null;

        // Get patient from appointment
        const patient = appointment?.patient_id
            ? await db.patients.get(appointment.patient_id)
            : null;

        // Get associated doctor (who is being charged)
        const associatedDoctor = facility_charge.associated_doctor_id
            ? await db.users.get(facility_charge.associated_doctor_id)
            : null;

        // Get treating doctor (from appointment)
        const treatingDoctor = appointment?.doctor_id
            ? await db.users.get(appointment.doctor_id)
            : null;

        // Get clinic info for the charge
        const clinic = facility_charge.clinic_id
            ? await db.clinics.get(facility_charge.clinic_id)
            : null;

        return { facility_charge, appointment, patient, associatedDoctor, treatingDoctor, clinic };
    }, [chargeId]);

    // Determine if current user is payer (associated doctor = being charged) or receiver
    const isPayer = useMemo(() => {
        if (!data || !doctorId) return false;
        return data.facility_charge.associated_doctor_id === doctorId;
    }, [data, doctorId]);

    const handleMarkPaid = async (e) => {
        e.preventDefault();
        try {
            const timestamp = new Date().toISOString();
            await db.facility_charges.update(chargeId, {
                paid_status: 'paid',
                payment_date: paymentDate,
                updated_at: timestamp,
                _synced: false
            });

            await syncService.addToQueue('facility_charge_payment', 'facility_charge_payment', {
                charge_ids: [chargeId],
                payment_date: paymentDate
            });

            toast.success("Facility charge marked as paid!");
            setShowPayDialog(false);
            setPaymentDate("");
        } catch (error) {
            console.error(error);
            toast.error("Failed to process payment");
        }
    };

    const handleDelete = async () => {
        if (!data?.facility_charge) return;

        if (!window.confirm("Are you sure you want to delete this facility_charge record?")) {
            return;
        }

        try {
            await db.facility_charges.delete(chargeId);
            await syncService.addToQueue('delete', 'facility_charge', { id: chargeId });
            toast.success("Facility charge deleted successfully!");
            navigate(-1);
        } catch (error) {
            console.error(error);
            toast.error("Failed to delete facility_charge");
        }
    };

    if (!data) {
        return (
            <div className="page-container">
                <Sidebar active="facility_charges" onLogout={onLogout} />
                <div className="main-content">
                    <div className="header">
                        <h1 style={{ textAlign: 'center', width: '100%', color: '#64748b' }}>Loading...</h1>
                    </div>
                </div>
            </div>
        );
    }

    const { facility_charge, appointment, patient, associatedDoctor, treatingDoctor, clinic } = data;
    const canModify = isPayer && facility_charge.paid_status === 'pending';

    return (
        <div className="page-container" data-testid="facility_charge-detail-page">
            <Sidebar active="facility_charges" onLogout={onLogout} />
            <div className="main-content">
                <div className="header" style={{ marginBottom: '1.5rem' }}>
                    <Button
                        variant="ghost"
                        onClick={() => navigate(-1)}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <ArrowLeft size={20} style={{ marginRight: '0.5rem' }} />
                        Back
                    </Button>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <h1 style={{ fontSize: '1.5rem', fontWeight: '700' }}>Charge Details</h1>
                        <SyncIndicator show={!facility_charge._synced} />
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '1.5rem' }}>
                    {/* Facility Charge Summary Card */}
                    <div className="card">
                        <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#10b981' }}>
                            <DollarSign size={18} />
                            {isPayer ? 'Facility Charge Payable' : 'Facility Charge Earned'}
                        </h2>
                        <div style={{ padding: '1.5rem', background: isPayer ? '#fef3c7' : '#d1fae5', borderRadius: '12px', textAlign: 'center' }}>
                            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: isPayer ? '#92400e' : '#059669', marginBottom: '0.5rem' }}>
                                {formatRupees(facility_charge.charge_amount)}
                            </div>
                            <span className={`badge badge-${facility_charge.paid_status}`} style={{ fontSize: '0.875rem' }}>
                                {facility_charge.paid_status === 'paid' ? (isPayer ? 'Paid' : 'Received') : 'Pending'}
                            </span>
                            {facility_charge.payment_date && (
                                <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.75rem' }}>
                                    {isPayer ? 'Paid on' : 'Received on'}: {new Date(facility_charge.payment_date).toLocaleDateString()}
                                </div>
                            )}
                        </div>

                        {/* Actions for payer */}
                        {canModify && (
                            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
                                <Button
                                    onClick={() => setShowPayDialog(true)}
                                    style={{
                                        flex: 1,
                                        background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: '0.5rem'
                                    }}
                                >
                                    <CheckCircle size={18} />
                                    Mark as Paid
                                </Button>
                                <Button
                                    variant="outline"
                                    onClick={handleDelete}
                                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                                    style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}
                                >
                                    <Trash2 size={18} />
                                    Delete
                                </Button>
                            </div>
                        )}
                    </div>

                    {/* Other Doctor Info Card */}
                    <div className="card">
                        <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#8b5cf6' }}>
                            <UserPlus size={18} />
                            {isPayer ? 'Associated Doctor' : 'From Doctor'}
                        </h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                            <div style={{ width: '48px', height: '48px', background: '#ede9fe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <UserPlus size={24} color="#7c3aed" />
                            </div>
                            <div>
                                <div style={{ fontWeight: '700', fontSize: '1.125rem' }}>
                                    {isPayer ? (associatedDoctor?.name || treatingDoctor?.name || 'Unknown') : (treatingDoctor?.name || facility_charge.treating_doctor_name || 'Unknown')}
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                    {isPayer ? (associatedDoctor?.phone || treatingDoctor?.phone || '-') : (treatingDoctor?.phone || facility_charge.treating_doctor_phone || '-')}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Appointment Context Card */}
                    <div
                        className="card"
                        onClick={() => isPayer && appointment && navigate(`/appointments/${appointment.id}`)}
                        style={{ cursor: isPayer && appointment ? 'pointer' : 'default' }}
                    >
                        <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#0ea5e9' }}>
                            <Calendar size={18} />
                            Appointment Details
                        </h2>
                        {appointment ? (
                            <div className="space-y-3">
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Date & Time</div>
                                    <div style={{ fontWeight: '600' }}>{new Date(appointment.date_time).toLocaleString()}</div>
                                </div>
                                {isPayer && (
                                    <>
                                        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                            <div>
                                                <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Status</div>
                                                <span className={`badge badge-${appointment.status}`}>{appointment.status}</span>
                                            </div>
                                            <div style={{ textAlign: 'right' }}>
                                                <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Fee</div>
                                                <div style={{ fontWeight: '700', fontSize: '1.125rem', color: '#0ea5e9' }}>{formatRupees(appointment.fee_amount || 0)}</div>
                                            </div>
                                        </div>
                                        {appointment.notes && (
                                            <div style={{ padding: '0.75rem', background: '#f8fafc', borderRadius: '8px', borderLeft: '3px solid #cbd5e1' }}>
                                                <div style={{ color: '#64748b', fontSize: '0.75rem', marginBottom: '0.25rem' }}>Notes</div>
                                                <div style={{ fontSize: '0.875rem' }}>{appointment.notes}</div>
                                            </div>
                                        )}
                                    </>
                                )}
                                {!isPayer && (
                                    <div style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                                        Limited view - appointment conducted by another doctor
                                    </div>
                                )}
                            </div>
                        ) : facility_charge.appointment_date ? (
                            <div className="space-y-3">
                                <div>
                                    <div style={{ color: '#64748b', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.25rem' }}>Date & Time</div>
                                    <div style={{ fontWeight: '600' }}>{new Date(facility_charge.appointment_date).toLocaleString()}</div>
                                </div>
                                <div style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                                    Appointment conducted by another doctor
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>
                                Appointment information not available
                            </div>
                        )}
                    </div>

                    {/* Patient Context Card */}
                    <div
                        className="card"
                        onClick={() => isPayer && patient && navigate(`/patients/${patient.id}`)}
                        style={{ cursor: isPayer && patient ? 'pointer' : 'default' }}
                    >
                        <h2 style={{ fontSize: '1.125rem', fontWeight: '600', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#64748b' }}>
                            <User size={18} />
                            Patient Information
                        </h2>
                        {patient ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '48px', height: '48px', background: '#dbeafe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <User size={24} color="#1e40af" />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '1.125rem' }}>{patient.name}</div>
                                    {isPayer ? (
                                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                                            {getDisplayAge(patient) ? `${getDisplayAge(patient)}y` : '—'} • {patient.gender} • {patient.phone}
                                        </div>
                                    ) : (
                                        <div style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                                            Referred patient
                                        </div>
                                    )}
                                </div>
                            </div>
                        ) : facility_charge.patient_name ? (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                                <div style={{ width: '48px', height: '48px', background: '#dbeafe', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <User size={24} color="#1e40af" />
                                </div>
                                <div>
                                    <div style={{ fontWeight: '700', fontSize: '1.125rem' }}>{facility_charge.patient_name}</div>
                                    <div style={{ fontSize: '0.875rem', color: '#64748b', fontStyle: 'italic' }}>
                                        Referred patient
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#94a3b8' }}>
                                Patient information not available
                            </div>
                        )}
                    </div>
                </div>

                {/* Timestamps */}
                <div style={{ marginTop: '1.5rem', fontSize: '0.75rem', color: '#94a3b8', textAlign: 'center' }}>
                    Created: {new Date(facility_charge.created_at).toLocaleString()}
                    {facility_charge.updated_at !== facility_charge.created_at && (
                        <span> • Updated: {new Date(facility_charge.updated_at).toLocaleString()}</span>
                    )}
                </div>
            </div>

            {/* Mark as Paid Dialog */}
            <Dialog open={showPayDialog} onOpenChange={setShowPayDialog}>
                <DialogContent data-testid="payment-dialog">
                    <DialogHeader>
                        <DialogTitle>Record Settlement</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleMarkPaid}>
                        <div style={{ marginBottom: '1rem' }}>
                            <Label htmlFor="payment_date">Settlement Date</Label>
                            <Input
                                id="payment_date"
                                data-testid="payment-date-input"
                                type="date"
                                value={paymentDate}
                                onChange={(e) => setPaymentDate(e.target.value)}
                                required
                            />
                        </div>
                        <div style={{ padding: '1rem', background: '#f8fafc', borderRadius: '8px', marginBottom: '1rem' }}>
                            <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.5rem' }}>Amount to be Paid</div>
                            <div style={{ fontSize: '1.5rem', fontWeight: '700' }}>
                                {formatRupees(facility_charge.charge_amount)}
                            </div>
                            {clinic && (
                                <div style={{ fontSize: '0.875rem', color: '#64748b', marginTop: '0.25rem' }}>
                                    To: {clinic.name}
                                </div>
                            )}
                        </div>
                        <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                            <Button type="button" variant="outline" onClick={() => setShowPayDialog(false)}>
                                Cancel
                            </Button>
                            <Button
                                data-testid="confirm-payment-button"
                                type="submit"
                                style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}
                            >
                                Confirm Settlement
                            </Button>
                        </div>
                    </form>
                </DialogContent>
            </Dialog>
        </div>
    );
}

FacilityChargeDetail.propTypes = {
    onLogout: PropTypes.func
};
